
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function formatDate(date: string | Date): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTime(date: string | Date | false | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(date: string | Date): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'الآن';
  if (minutes < 60) return `منذ ${minutes} دقيقة`;
  if (hours < 24) return `منذ ${hours} ساعة`;
  if (days < 7) return `منذ ${days} يوم`;
  return formatDate(d);
}

export function generateId(): string {
  return crypto.randomUUID();
}

export function cleanAddress(addr: string | false | undefined | null): string {
  if (!addr) return '';
  const str = String(addr).trim();
  if (str.includes('<') && str.includes('>')) {
    try {
      const doc = new DOMParser().parseFromString(str, 'text/html');
      const text = doc.body.textContent || doc.body.innerText || '';
      return text.trim();
    } catch (e) {
      return str.replace(/<[^>]*>/g, '').trim();
    }
  }
  return str;
}

// ============ Arabic Text Normalization for Search ============

/**
 * Normalize Arabic text for search comparison
 * Handles: أ→ا, إ→ا, آ→ا, ى→ي, ة→ه, و→ؤ, ت→ة
 * Removes diacritics (tashkeel)
 */
export function normalizeArabic(text: string): string {
  if (!text) return '';
  let normalized = text;

  // Alef variations → bare Alef
  normalized = normalized.replace(/[أإآٱ]/g, 'ا');

  // Alef Maksura → Ya
  normalized = normalized.replace(/ى/g, 'ي');

  // Ta Marbuta → Ha
  normalized = normalized.replace(/ة/g, 'ه');

  // Waw with Hamza → Waw
  normalized = normalized.replace(/ؤ/g, 'و');

  // Ya with Hamza → Ya
  normalized = normalized.replace(/ئ/g, 'ي');

  // Remove Arabic diacritics (tashkeel)
  normalized = normalized.replace(/[ً-ٰٟ]/g, '');

  return normalized.toLowerCase().trim();
}

/**
 * Normalize text for robust search (Arabic + general)
 */
export function normalizeForSearch(text: string | null | undefined): string {
  if (!text) return '';
  return normalizeArabic(text.trim());
}

/**
 * Check if two Arabic texts match after normalization
 */
export function arabicTextMatch(text1: string, text2: string): boolean {
  return normalizeForSearch(text1) === normalizeForSearch(text2);
}

/**
 * Check if text contains query after Arabic normalization
 */
export function arabicTextContains(text: string, query: string): boolean {
  return normalizeForSearch(text).includes(normalizeForSearch(query));
}

// ============ Phone Number Normalization ============

/**
 * Normalize phone number: remove spaces, country codes, keep digits only
 * Returns clean number like: 01001234567
 */
export function normalizePhoneNumber(phone: string | false | undefined | null): string {
  if (!phone) return '';
  let cleaned = String(phone).replace(/[^\d+]/g, ''); // Remove all non-digit except +

  // Remove Egyptian country code
  if (cleaned.startsWith('+20')) {
    cleaned = cleaned.substring(3);
  } else if (cleaned.startsWith('0020')) {
    cleaned = cleaned.substring(4);
  } else if (cleaned.startsWith('20') && cleaned.length > 10) {
    cleaned = cleaned.substring(2);
  }

  // If starts with 1 and length is 10, add leading 0
  if (cleaned.length === 10 && cleaned.startsWith('1')) {
    cleaned = '0' + cleaned;
  }

  return cleaned;
}

/**
 * Clean phone for search comparison (digits only, no country code)
 */
export function cleanPhoneForSearch(phone: string | false | undefined | null): string {
  return normalizePhoneNumber(phone).replace(/[^\d]/g, '');
}

/**
 * Format phone for display: clean, without country code, without spaces
 * e.g., 01001234567
 */
export function formatPhoneDisplay(phone: string | false | undefined | null): string {
  return normalizePhoneNumber(phone);
}

/**
 * Check if a phone number matches a query (partial match after cleaning)
 */
export function phoneMatchesQuery(phone: string | false | undefined | null, query: string): boolean {
  const cleanPhone = cleanPhoneForSearch(phone);
  const cleanQuery = query.replace(/[^\d]/g, '');
  if (!cleanQuery) return false;
  return cleanPhone.includes(cleanQuery);
}

// ============ HTML / Message Helpers ============

/**
 * Strip HTML tags and decode entities from a string
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  let text = html;
  // Decode common HTML entities
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&apos;/g, "'");
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&nbsp;/g, ' ');
  // Decode numeric HTML entities &#1575;
  text = text.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code)));
  text = text.replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)));
  // Remove remaining HTML tags
  text = text.replace(/<[^>]*>/g, '');
  return text.trim();
}

/**
 * Check if a message body is effectively empty (no text after stripping HTML)
 */
export function isMessageEmpty(body: string | null | undefined): boolean {
  if (!body) return true;
  const stripped = stripHtml(body).replace(/\s+/g, '').trim();
  return stripped.length === 0;
}

/**
 * Get file type category from mimetype
 */
export function getFileCategory(mimetype: string | null | undefined): 'image' | 'video' | 'audio' | 'document' {
  if (!mimetype) return 'document';
  if (mimetype.startsWith('image/')) return 'image';
  if (mimetype.startsWith('video/')) return 'video';
  if (mimetype.startsWith('audio/')) return 'audio';
  return 'document';
}

/**
 * Format currency (EGP)
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return '';
  return `${amount.toFixed(2)} ج.م`;
}

/**
 * Format duration in minutes to human-readable string
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} دقيقة`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} ساعة`;
  return `${hours} ساعة ${mins} دقيقة`;
}
